/**
 * Internal forms in the Knowledgebase Hub (Jon, 2026-11-03): the JotForm forms
 * tagged "Omega Wolf" (internal requests, and request forms built for clients),
 * grouped by JotForm workspace. Each opens in JotForm; "Ask Lupa" opens the widget
 * with a prompt so Lupa collects the answers and returns a pre-filled link.
 * Submitting stays in JotForm until write access is approved. KB jotform.md.
 */
import React, { useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare, faBookOpen, faClipboardList, faWandMagicSparkles } from '@fortawesome/free-solid-svg-icons';
import { DWA } from '../theme/dwaTokens';
import { openLupaWidget } from '../hubs/hubNav';
import type { InternalForm } from '../services/kbApi';
import { groupByWorkspace, lupaFormPrompt, matchForms, teamworkLine, useInternalForms } from './internalFormsModel';
import { Panel } from '../hubs/HubCards';

const button: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.35rem',
  padding: '0.35rem 0.7rem',
  borderRadius: DWA.radius,
  fontSize: '0.78rem',
  fontWeight: 700,
  fontFamily: DWA.font,
  cursor: 'pointer',
  textDecoration: 'none',
  whiteSpace: 'nowrap',
};

const FormRow: React.FC<{ form: InternalForm; highlight?: boolean }> = ({ form, highlight }) => {
  const ref = useRef<HTMLLIElement>(null);
  useEffect(() => {
    if (highlight) ref.current?.scrollIntoView?.({ block: 'center' });
  }, [highlight]);
  return (
  <li ref={ref} data-testid={`form-row-${form.id}`} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', padding: '0.5rem 0.65rem', background: highlight ? DWA.goldSoft : '#f6f6f4', outline: highlight ? `2px solid ${DWA.gold}` : 'none', borderRadius: DWA.radius }}>
    <span style={{ flex: '1 1 14rem', minWidth: 0 }}>
      <span style={{ display: 'block', fontWeight: 600, fontSize: '0.88rem' }}>{form.title}</span>
      {form.description && <span style={{ display: 'block', fontSize: '0.78rem', color: DWA.textSecondary, marginTop: '0.1rem' }}>{form.description}</span>}
      <span data-testid={`form-teamwork-${form.id}`} style={{ display: 'block', fontSize: '0.72rem', color: DWA.textMuted, marginTop: '0.15rem' }}>
        {teamworkLine(form)}
      </span>
    </span>
    <a href={form.url} target="_blank" rel="noopener noreferrer" style={{ ...button, background: DWA.gold, color: DWA.onGold }}>
      Open form <FontAwesomeIcon icon={faArrowUpRightFromSquare} style={{ fontSize: '0.65rem' }} />
    </a>
    {form.page_url && /^https?:\/\//i.test(form.page_url) && (
      <a
        href={form.page_url}
        target="_blank"
        rel="noopener noreferrer"
        title="This form is also on this page"
        style={{ ...button, background: '#ffffff', color: DWA.ink, border: `1px solid ${DWA.lineLight}` }}
      >
        On page <FontAwesomeIcon icon={faArrowUpRightFromSquare} style={{ fontSize: '0.65rem' }} />
      </a>
    )}
    <button
      type="button"
      onClick={() => openLupaWidget(lupaFormPrompt(form))}
      aria-label={`Ask Lupa to help fill out ${form.title}`}
      style={{ ...button, background: '#ffffff', color: DWA.ink, border: `1px solid ${DWA.gold}` }}
    >
      <FontAwesomeIcon icon={faWandMagicSparkles} style={{ color: DWA.goldInk }} /> Ask Lupa
    </button>
  </li>
  );
};

/**
 * The forms list in the Knowledgebase's main column (Browse → Internal forms), or,
 * with `query`, the matching forms above the search results (hidden when none).
 */
export const InternalFormsPanel: React.FC<{ query?: string; highlight?: string }> = ({ query = '', highlight }) => {
  const { forms, error } = useInternalForms();
  const shown = forms ? matchForms(forms, query) : [];
  if (query && (!forms || shown.length === 0)) return null;

  return (
    <section
      data-testid="internal-forms"
      aria-label="Internal forms"
      style={{ marginTop: query ? '1.25rem' : 0, background: DWA.cardBg, borderRadius: DWA.radius, boxShadow: DWA.cardShadow, color: DWA.ink, padding: '1rem 1.15rem' }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.6rem' }}>
        <span style={{ width: 28, height: 28, borderRadius: DWA.radius, background: DWA.dark, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
          <FontAwesomeIcon icon={faClipboardList} style={{ color: DWA.gold, fontSize: '0.8rem' }} />
        </span>
        <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, flex: 1 }}>{query ? 'Matching internal forms' : 'Internal forms'}</h2>
      </div>
      {!query && (
        <p style={{ margin: '0 0 0.75rem', fontSize: '0.82rem', color: DWA.textSecondary }}>
          Request forms for the team and for clients. Open one to fill it out, or ask Lupa to help: it asks what it needs and gives you a pre-filled link to check and submit.
        </p>
      )}
      {error && <p style={{ margin: 0, fontSize: '0.85rem', color: DWA.textSecondary }}>Internal forms are unavailable right now.</p>}
      {!error && !forms && <p style={{ margin: 0, fontSize: '0.85rem', color: DWA.textSecondary }}>Loading forms…</p>}
      {forms && forms.length === 0 && (
        <p style={{ margin: 0, fontSize: '0.85rem', color: DWA.textSecondary }}>No forms yet. In JotForm, add the "Omega Wolf" label to a form to list it here.</p>
      )}
      {groupByWorkspace(shown).map(([workspace, list]) => (
        <div key={workspace} style={{ marginTop: '0.5rem' }}>
          <p style={{ margin: '0 0 0.35rem', fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: DWA.textSecondary }}>{workspace}</p>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
            {list.map((f) => (
              <FormRow key={f.id} form={f} highlight={f.id === highlight} />
            ))}
          </ul>
        </div>
      ))}
    </section>
  );
};

const sideLink: React.CSSProperties = {
  display: 'block',
  width: '100%',
  textAlign: 'left',
  border: 'none',
  background: 'none',
  padding: '0.3rem 0.45rem',
  borderRadius: DWA.radius,
  fontFamily: DWA.font,
  fontSize: '0.84rem',
  color: DWA.ink,
  cursor: 'pointer',
};

/** Browse sidebar: the forms by workspace (titles only); picking one opens the forms list. */
export const FormsSidebar: React.FC<{ active: boolean; selected?: string; onOpen: (formId?: string) => void }> = ({ active, selected, onOpen }) => {
  const { forms, error } = useInternalForms();
  return (
    <Panel
      icon={faClipboardList}
      title="Internal forms"
      action={
        <button type="button" onClick={() => onOpen()} style={{ ...sideLink, width: 'auto', color: DWA.goldInk, fontWeight: 700, fontSize: '0.78rem' }}>
          {active ? 'Showing' : 'View all'}
        </button>
      }
    >
      <div data-testid="forms-sidebar">
        {error && <p style={{ margin: 0, fontSize: '0.8rem', color: DWA.textSecondary }}>Unavailable right now.</p>}
        {!error && !forms && <p style={{ margin: 0, fontSize: '0.8rem', color: DWA.textSecondary }}>Loading…</p>}
        {forms && forms.length === 0 && <p style={{ margin: 0, fontSize: '0.8rem', color: DWA.textSecondary }}>No forms tagged yet.</p>}
        {forms &&
          groupByWorkspace(forms).map(([workspace, list]) => (
            <div key={workspace} style={{ marginBottom: '0.4rem' }}>
              <p style={{ margin: '0.2rem 0', fontSize: '0.66rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: DWA.textSecondary }}>{workspace}</p>
              {list.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => onOpen(f.id)}
                  aria-current={active && selected === f.id ? 'true' : undefined}
                  style={{ ...sideLink, background: active && selected === f.id ? DWA.goldSoft : 'none' }}
                >
                  {f.title.replace(/^[^|]+\|\s*/, '')}
                </button>
              ))}
            </div>
          ))}
      </div>
    </Panel>
  );
};

/** Browse sidebar: the Omega Wolf Guide. */
export const GuideSidebar: React.FC<{ onOpen: () => void }> = ({ onOpen }) => (
  <Panel icon={faBookOpen} title="Omega Wolf Guide">
    <p style={{ margin: '0 0 0.5rem', fontSize: '0.8rem', color: DWA.textSecondary, lineHeight: 1.5 }}>
      How Omega Wolf and Lupa work: every Hub, where the data comes from and when it refreshes.
    </p>
    <button type="button" onClick={onOpen} style={{ ...sideLink, width: 'auto', padding: '0.4rem 0.75rem', background: DWA.gold, color: DWA.onGold, fontWeight: 700 }}>
      Open the Quick start
    </button>
  </Panel>
);
